'use client'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/use-toast'
import usePostcode from '@/hooks/usePostCodePopup'
import { validateTel, zStringRequired } from '@/lib/validation'
import { useGuestCreate } from '@/service/queries/useGuests'
import { getOnlyNumberStr, replacePhoneNumber } from '@/utils'
import { zodResolver } from '@hookform/resolvers/zod'
import { Control, FieldValues, Path, useForm } from 'react-hook-form'
import { z } from 'zod'

const zPhoneNumber = () =>
  zStringRequired().refine(validateTel, { message: '연락처 형식을 확인해주세요.' })

const requiredTerms = [
  { name: 'termsOfUse', type: 'TERMS_OF_USE', label: '이용약관' },
  { name: 'privacyPolicy', type: 'PRIVACY_POLICY', label: '개인정보처리방침' },
  { name: 'refundPolicy', type: 'REFUND_POLICY', label: '환불규정' },
] as const
const zAgreement = () => z.boolean().refine(Boolean, '필수 약관에 동의해주세요.')

export const createSchema = z.object({
  phoneNumber: zPhoneNumber(),
  name: zStringRequired().trim().min(1, '이름을 입력해주세요.'),
  password: zStringRequired().min(4, '비밀번호는 4자 이상 입력해주세요.'),
  email: z.string().email('이메일 형식을 확인해주세요.').or(z.literal('')).optional(),
  address: z.string().optional(),
  addressDetail: z.string().optional(),
  termsOfUse: zAgreement(),
  privacyPolicy: zAgreement(),
  refundPolicy: zAgreement(),
})

type TCreateForm = z.infer<typeof createSchema>
type TCreatedGuest = Pick<TCreateForm, 'name' | 'phoneNumber' | 'email'> & { id: number }

interface IProductGuestDialogProps {
  open: boolean
  termsPath: string
  companyId?: number
  onOpenChange: (open: boolean) => void
  onCreated: (guest: TCreatedGuest) => void
}

/** 비회원 예약용 게스트 등록 */
export default function ProductGuestDialog({
  open,
  termsPath,
  companyId,
  onOpenChange,
  onCreated,
}: IProductGuestDialogProps) {
  const handleCreated = (guest: TCreatedGuest) => {
    toast({ description: `${guest.name}님, 게스트 정보가 등록되었습니다.` })
    onCreated(guest)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-[14px] text-black lg:text-[20px]">예약하기</DialogTitle>
          <DialogDescription>예약을 위한 정보를 입력해주세요.</DialogDescription>
        </DialogHeader>
        {!companyId ? (
          <p className="pb-[48px] text-sm text-gray-500">
            예약을 위한 정보를 입력할 수 없습니다. 잠시 후 다시 시도해주세요.
          </p>
        ) : (
          <GuestCreateForm termsPath={termsPath} companyId={companyId} onCreated={handleCreated} />
        )}
      </DialogContent>
    </Dialog>
  )
}

interface IGuestFormProps {
  termsPath: string
  companyId: number
  onCreated: (guest: TCreatedGuest) => void
}

const GuestCreateForm = ({ companyId, termsPath, onCreated }: IGuestFormProps) => {
  const createMutation = useGuestCreate()
  const { openSearchPostcodePopup } = usePostcode()
  const form = useForm<TCreateForm>({
    resolver: zodResolver(createSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      phoneNumber: '',
      password: '',
      email: '',
      address: '',
      addressDetail: '',
      termsOfUse: false,
      privacyPolicy: false,
      refundPolicy: false,
    },
  })
  const address = form.watch('address')
  const agreements = form.watch(['termsOfUse', 'privacyPolicy', 'refundPolicy'])
  const allAgreed = agreements.every(Boolean)
  const searchAddress = () =>
    openSearchPostcodePopup(({ address }) =>
      form.setValue('address', address, { shouldDirty: true })
    )

  const onSubmit = async ({
    email,
    address,
    addressDetail,
    password,
    name,
    phoneNumber,
  }: TCreateForm) => {
    if (createMutation.isPending) return
    const { data } = await createMutation.mutateAsync({
      companyId,
      name,
      phoneNumber,
      password,
      email: email || null,
      address: [address, addressDetail?.trim()].filter(Boolean).join(' ') || null,
    })
    onCreated({ id: data.id, name, phoneNumber, email })
  }

  return (
    <Form {...form}>
      <form
        noValidate
        onSubmit={e => {
          e.stopPropagation()
          form.handleSubmit(values => onSubmit(values).catch(() => {}))(e)
        }}
        className="w-full space-y-[8px] pb-[30px]"
      >
        <FormField
          control={form.control}
          name="name"
          required
          vertical
          render={({ field }) => (
            <FormItem>
              <FormLabel>이름</FormLabel>
              <div className="w-full">
                <FormControl>
                  <Input placeholder="이름을 입력해주세요." autoComplete="name" {...field} />
                </FormControl>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
        <PhoneField control={form.control} name="phoneNumber" />
        <FormField
          control={form.control}
          name="password"
          required
          vertical
          render={({ field }) => (
            <FormItem>
              <FormLabel>비밀번호</FormLabel>
              <div className="w-full">
                <FormControl>
                  <Input
                    type="password"
                    placeholder="비밀번호를 입력해주세요."
                    autoComplete="new-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          vertical
          render={({ field }) => (
            <FormItem>
              <FormLabel>이메일</FormLabel>
              <div className="w-full">
                <FormControl>
                  <Input
                    type="email"
                    placeholder="이메일을 입력해주세요. (선택)"
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="address"
          vertical
          render={({ field }) => (
            <FormItem>
              <FormLabel>주소</FormLabel>
              <div className="flex w-full gap-2">
                <FormControl>
                  <Input
                    readOnly
                    placeholder="주소를 검색해주세요. (선택)"
                    className="cursor-pointer"
                    {...field}
                    onClick={searchAddress}
                  />
                </FormControl>
                <Button
                  type="button"
                  variant="outline"
                  size="md"
                  className="shrink-0 rounded-lg"
                  onClick={searchAddress}
                >
                  주소 검색
                </Button>
              </div>
            </FormItem>
          )}
        />
        {!!address && (
          <FormField
            control={form.control}
            name="addressDetail"
            vertical
            render={({ field }) => (
              <FormItem>
                <FormLabel className="sr-only">상세 주소</FormLabel>
                <FormControl>
                  <Input placeholder="상세 주소를 입력해주세요." {...field} />
                </FormControl>
              </FormItem>
            )}
          />
        )}

        <fieldset className="space-y-4 pt-6">
          <legend className="sr-only">필수 약관 동의</legend>
          <label className="flex cursor-pointer items-center gap-3 border-b border-gray-200 pb-4 font-semibold">
            <Checkbox
              className="bg-white"
              checked={allAgreed ? true : agreements.some(Boolean) ? 'indeterminate' : false}
              onCheckedChange={checked => {
                requiredTerms.forEach(({ name }) =>
                  form.setValue(name, checked === true, { shouldDirty: true, shouldValidate: true })
                )
              }}
            />
            약관 전체 동의
          </label>
          {requiredTerms.map(({ name, type, label }) => (
            <FormField
              key={name}
              control={form.control}
              name={name}
              render={({ field }) => (
                <div className="flex items-center justify-between gap-3 text-sm">
                  <label className="flex cursor-pointer items-center gap-3">
                    <Checkbox
                      className="bg-white"
                      checked={field.value}
                      onCheckedChange={checked => field.onChange(checked === true)}
                      onBlur={field.onBlur}
                      ref={field.ref}
                    />
                    {label} 동의 (필수)
                  </label>
                  <a
                    href={`/terms?${new URLSearchParams({ type, path: termsPath })}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="shrink-0 text-gray-400 hover:underline"
                    aria-label={`${label} 내용보기 (새 탭)`}
                  >
                    내용보기
                  </a>
                </div>
              )}
            />
          ))}
        </fieldset>

        <DialogFooter className="flex justify-center pb-0">
          <Button
            type="submit"
            size="md"
            className="w-full !min-w-fit grow-0"
            disabled={createMutation.isPending || !form.formState.isValid}
          >
            예약하기
          </Button>
        </DialogFooter>
      </form>
    </Form>
  )
}

interface IFieldProps<T extends FieldValues> {
  control: Control<T>
  name: Path<T>
}

const PhoneField = <T extends FieldValues>({ control, name }: IFieldProps<T>) => (
  <FormField
    control={control}
    name={name}
    required
    vertical
    render={({ field }) => (
      <FormItem>
        <FormLabel>연락처</FormLabel>
        <div className="w-full">
          <FormControl>
            <Input
              type="tel"
              placeholder="연락처를 입력해주세요."
              autoComplete="tel"
              {...field}
              value={replacePhoneNumber(field.value)}
              onChange={e => field.onChange(getOnlyNumberStr(e.target.value))}
            />
          </FormControl>
          <FormMessage />
        </div>
      </FormItem>
    )}
  />
)
